import * as THREE from 'three';

// ─── Skin tone palette (linear, not gamma) ─────────────────────────────────
const SKIN_TONES = [
  [0.97, 0.84, 0.68],
  [0.91, 0.73, 0.54],
  [0.83, 0.61, 0.41],
  [0.73, 0.49, 0.31],
  [0.62, 0.39, 0.22],
  [0.50, 0.29, 0.14],
  [0.37, 0.20, 0.09],
  [0.23, 0.12, 0.05],
];

export function getSkinColor(t) {
  const n = SKIN_TONES.length - 1;
  const fi = Math.min(n - 0.001, t * n);
  const i = Math.floor(fi);
  const f = fi - i;
  const c0 = SKIN_TONES[i];
  const c1 = SKIN_TONES[i + 1];
  return [c0[0] + (c1[0] - c0[0]) * f, c0[1] + (c1[1] - c0[1]) * f, c0[2] + (c1[2] - c0[2]) * f];
}

export function getAreolaColor(t) {
  const [r, g, b] = getSkinColor(t);
  const dark = 0.42 + t * 0.18;
  return [r * dark + 0.06, g * dark * 0.55, b * dark * 0.42];
}

const smoothStep = (e0, e1, x) => {
  const t = Math.max(0, Math.min(1, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
};

// ─── Vertex computation ─────────────────────────────────────────────────────
function computeVertex(theta, phi, params) {
  const { volume, sag, nippleShape, nipplePlateau, nippleSize, areolRadius } = params;
  const st = Math.sin(theta), ct = Math.cos(theta);
  const sp = Math.sin(phi), cp = Math.cos(phi);
  const frontFactor = Math.max(0, cp);

  // Base: +z = front, +x = right, +y = up
  // Dynamic spread (tilt outwards) based on volume
  // spread = 0 when volume is at minimum (around 0.35)
  const spread = Math.max(0, (volume - 0.35) * 0.22); 
  const cosS = Math.cos(spread), sinS = Math.sin(spread);

  // Rotate around Y-axis to spread outwards
  const rx = st * sp;
  const rz = st * cp;
  let x = rx * cosS - rz * sinS;
  let y = ct;
  let z = rx * sinS + rz * cosS;

  // 1. Extra volume stretch forward towards the nipple:
  // The nipple area (frontFactor) pulls forward more noticeably as volume increases.
  const volStretch = (params.volume - 0.5) * 0.25 * Math.pow(frontFactor, 2.0);
  if (volStretch > 0.0) {
    z += volStretch * cosS;
    x -= volStretch * sinS;
  }

  // 1. Front protrusion
  // 2. Flatten back
  const backFactor = Math.max(0, -cp);
  z *= (1.0 - backFactor * 0.75);
  x *= (1.0 - backFactor * 0.08);

  // 3. Wider oval cross-section
  x *= 1.18;

  // Physiological baseline: lower hemisphere is naturally fuller and naturally drops slightly
  // Parametrized via lowerWidth
  const lWdt = params.lowerWidth !== undefined ? params.lowerWidth : 0.5;

  const lowerVolFactor = Math.pow(Math.max(0, -ct), 1.5);
  x *= 1.0 + (lowerVolFactor * 0.4 * lWdt);

  // 4. Sag/Gravity: smoothly lower the front-facing tissue (nipple area)
  // Use st (sin theta) and frontFactor to maximize effect at the nipple.
  // This ensures the back stays fixed while the front tip drops.
  const f2 = Math.pow(frontFactor, 2);
  const sagEffect = st * sag * f2;
  
  // The nipple and surrounding front tissue smoothly drop downwards.
  y -= sagEffect * 0.95; 

  // To preserve volume and emulate weight, the upper half flattens slightly
  // while the lower half bulges outward and sags.
  // ct is 1 at the top, 0 at the nipple, -1 at the bottom.
  z += sagEffect * (0.15 - ct * 0.35);

  // Apply volume
  x *= volume; y *= volume; z *= volume;

  // 5. Nipple / Areola bump
  const dt = theta - Math.PI * 0.5;
  const rawDp = phi % (2 * Math.PI);
  const dp = Math.min(rawDp, 2 * Math.PI - rawDp);
  const dist = Math.sqrt(dt * dt + dp * dp);

  const areolAng = 0.21 + areolRadius * 0.23;
  const nippleAng = 0.07;

  if (dist < areolAng + 0.05) {
    const len = Math.sqrt(x * x + y * y + z * z) || 1;
    const nx = x / len, ny = y / len, nz = z / len;

    if (dist < nippleAng) {
      // New Shape logic: Flat
      const plateauDist = nipplePlateau * 0.065;
      
      let t;
      if (dist < plateauDist) {
        t = 1.0; // Flat plateau
      } else {
        // Softened falloff from plateau edge to nipple edge
        const falloff = (dist - plateauDist) / (nippleAng - plateauDist);
        t = 1.0 - smoothStep(0.0, 1.0, falloff);
        t = Math.pow(t, 1.4); // Slightly rounded top edge
      }
      
      const bump = nippleSize * 0.16 * volume * t;
      x += nx * bump; y += ny * bump; z += nz * bump;
    } else if (dist < areolAng) {
      const at = (dist - nippleAng) / (areolAng - nippleAng);
      const rim = 0.011 * Math.sin(at * Math.PI) * volume;
      x += nx * rim; y += ny * rim; z += nz * rim;
    }
  }

  return [x, y, z];
}

// ─── Geometry builder ───────────────────────────────────────────────────────
export function createBreastGeometry(params, segments = 180) {
  const rows = segments, cols = segments * 2;
  const positions = [], uvs = [], indices = [];

  for (let i = 0; i <= rows; i++) {
    const theta = (i / rows) * Math.PI;
    for (let j = 0; j <= cols; j++) {
      const phi = (j / cols) * Math.PI * 2;
      const [x, y, z] = computeVertex(theta, phi, params);
      positions.push(x, y, z);
      uvs.push(j / cols, i / rows);
    }
  }

  for (let i = 0; i < rows; i++) {
    for (let j = 0; j < cols; j++) {
      const a = i * (cols + 1) + j;
      const b = a + 1;
      const c = (i + 1) * (cols + 1) + j;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geom.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  geom.setIndex(indices);
  geom.computeVertexNormals();

  // Average normals along the seam (j=0 and j=cols) to remove sharp lines
  const normals = geom.getAttribute('normal');
  for (let i = 0; i <= rows; i++) {
    const idx0 = i * (cols + 1) + 0;
    const idxN = i * (cols + 1) + cols;
    const nx = (normals.getX(idx0) + normals.getX(idxN)) * 0.5;
    const ny = (normals.getY(idx0) + normals.getY(idxN)) * 0.5;
    const nz = (normals.getZ(idx0) + normals.getZ(idxN)) * 0.5;
    const len = Math.sqrt(nx*nx + ny*ny + nz*nz);
    if (len > 0.000001) {
      normals.setXYZ(idx0, nx/len, ny/len, nz/len);
      normals.setXYZ(idxN, nx/len, ny/len, nz/len);
    } else {
      normals.setXYZ(idx0, 0, 1, 0);
      normals.setXYZ(idxN, 0, 1, 0);
    }
  }
  normals.needsUpdate = true;

  return geom;
}

// ─── Mirror geometry for second breast ─────────────────────────────────────
export function mirrorGeometry(geom) {
  const mirrored = geom.clone();
  const pos = mirrored.getAttribute('position');
  for (let i = 0; i < pos.count; i++) pos.setX(i, -pos.getX(i));
  pos.needsUpdate = true;

  const idx = mirrored.getIndex();
  if (idx) {
    const arr = idx.array;
    for (let i = 0; i < arr.length; i += 3) {
      const t = arr[i + 1]; arr[i + 1] = arr[i + 2]; arr[i + 2] = t;
    }
    idx.needsUpdate = true;
  }
  mirrored.computeVertexNormals();
  return mirrored;
}
